import { computeE1rmKg } from "./pr.js";
import { EFFORT_SUBMAXIMAL_RIR, effectiveRir } from "./effortScale.js";
import type { LogSetKind } from "./schemas/logSet.js";

// How much an e1RM point PROVES — the missing half of `e1rmKg`.
//
// The stamped `e1rmKg` (pr.ts computeE1rmKg, stamped server-side in
// setFactory) is just "the number": it says nothing about how it was
// earned. Two 140 kg e1RMs are not the same datum — one from a set with
// three reps left in the tank is a floor the athlete never touched, one
// from a set taken to the limit is a floor they actually stood on. Every
// consumer that DERIVES from e1RM (regression flags, MAX lower/raise,
// deload suggestion) is already making that distinction, each with its
// own inline effort test. This module says it once, in the vocabulary of
// what the point proves rather than of how it felt.
//
// Nothing consumes it yet — it ships as a pure classifier with its tests
// (measurement contract, slice 1). Wiring it changes numbers the family
// already sees (MAX proposals, trend arrows), so each callsite is its own
// gated decision, not a side effect of landing this file.

// The closed set of verdicts. ⛔ NOT a scale: never compare these with
// </> and never index them by position. `unknown` is a THIRD STATE, not
// a low score — an untagged set is a set we know nothing about, and the
// one mistake this module exists to prevent is collapsing it into
// "submaximal" (i.e. treating silence as "it was easy"). Same
// tuple-derived-union shape as CALORIE_CONFIDENCES (calorieEstimate.ts),
// so a future persisted field can derive its zod enum from here instead
// of hand-mirroring the literals.
export const E1RM_CONFIDENCES = [
  // No e1RM exists for this set at all — not weighted, no load, or reps
  // outside the Epley window. There is nothing to be confident about.
  "no-estimate",
  // An e1RM exists but effort is untagged. Unknowable, not weak.
  "unknown",
  // Effort tagged, reps left in reserve — the number UNDER-states capacity.
  "submaximal",
  // Near-maximal (RIR ≤ EFFORT_SUBMAXIMAL_RIR): close to a real floor.
  "probable",
  // Taken to the limit on the open variable: a genuine lower bound.
  "proven",
] as const;
export type E1rmConfidence = (typeof E1RM_CONFIDENCES)[number];

// Classify one set's e1RM point.
//
// Accepts the same flat LogSet-style shape computeE1rmKg does, so a
// caller that just stamped an e1RM can pass the very same object — and
// so eligibility ("is there a point here?") is DELEGATED to
// computeE1rmKg rather than re-tested. That delegation is the point: a
// second, hand-rolled copy of the weighted/reps/weight gate is exactly
// the drift pr.ts's header records from 0.83.0, and it would let this
// module call "proven" a set the chart never plots.
//
// `amrap` is the logged flag (the athlete took the open variable to the
// limit: reps under a fixed load for strength, and later time/distance
// for holds and cardio — ONE flag for all three forms). Not to be
// confused with the PRESCRIPTION-side `amrap` on SetPrescription
// (programResolution.ts), which is a target, not an outcome.
//
// Effort resolution goes through effectiveRir, never re-testing `fail`
// or re-deriving the rpe fallback inline (effortScale.ts is the single
// source; `fail` already folds to RIR 0 there).
export const e1rmConfidence = (set: {
  kind: LogSetKind;
  reps?: number;
  weight?: number;
  amrap?: boolean;
  fail?: boolean;
  rir?: number | null;
  rpe?: number | null;
  // #534: passed straight to computeE1rmKg, so a form-break set grades as
  // "no-estimate", the same answer the chart gives it.
  tags?: readonly string[];
}): E1rmConfidence => {
  // Reps window / kind / load first: an AMRAP that ran to 25 reps really
  // did go to the limit, but Epley cannot extrapolate that far, so there
  // is no point to grade. Going to the limit and being MEASURABLE are
  // two different claims and this ordering keeps them apart.
  if (computeE1rmKg(set) === null) return "no-estimate";

  // The estimate exists; the rest is grading its effort. Delegated so a
  // consumer that ALREADY knows a point exists (the e1rmPoints funnel,
  // which pre-filters on topE1rmKg > 0) grades it through the exact same
  // ladder rather than re-rolling it — the drift this whole module exists
  // to prevent.
  return e1rmConfidenceFromEffort({ amrap: set.amrap, rir: effectiveRir(set) });
};

/** The effort-grading tail of {@link e1rmConfidence}, for a point ALREADY
 *  known to carry an e1RM (so `no-estimate` is impossible here). Callers that
 *  hold a resolved effort but not the raw set — the `e1rmPoints` funnel, whose
 *  aggregate keeps `topSetRir` (fail already folded to 0) but not the full set —
 *  grade through this instead of reconstructing a synthetic set or copying the
 *  RIR ladder. `rir` must be the EFFECTIVE rir (fail⇒0, rpe→rir already applied),
 *  the same value `effectiveRir` produces. */
export const e1rmConfidenceFromEffort = (opts: {
  amrap?: boolean;
  rir: number | null;
}): Exclude<E1rmConfidence, "no-estimate"> => {
  // AMRAP wins over a contradictory self-report: the flag is a structural
  // statement about how the set was run (the variable was left open),
  // while rir is an estimate of what was left in it — if the reps were
  // open, there was nothing left by definition. Also the reason the flag
  // carries signal RIR cannot: an AMRAP with no effort tag at all is
  // still a proven floor.
  if (opts.amrap === true) return "proven";

  // Deliberately first and deliberately not `!rir`: RIR 0 and "no effort
  // recorded" are different worlds, and only one of them proves a ceiling.
  if (opts.rir === null) return "unknown";
  // RIR 0 (or fail, folded above) — the same ceiling-attempt predicate
  // maxRegression.isCeilingAttempt gates the MAX-lower path on.
  if (opts.rir === 0) return "proven";
  return opts.rir <= EFFORT_SUBMAXIMAL_RIR ? "probable" : "submaximal";
};
